import {
  Alert,
  Box,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  monthRange,
  type ApplicantStage,
  type ReferralListFilters,
  type ReferralReportRow,
  type ReferralReports,
} from '@hv/shared';
import { useCycles, useReferralReports } from '../api/hooks';
import { describeError } from '../components/Notify';
import { referralsHref } from '../referrals/filters';
import { DrillCount, plural, ReportsNote } from '../referrals/ReferralDashboardSection';
import { useUrlState } from '../urlState';

type Drill = (row: ReferralReportRow) => Omit<ReferralListFilters, 'cycleId'>;

interface ReportSpec {
  key: keyof Pick<ReferralReports, 'byGrade' | 'bySchool' | 'byTrustee' | 'byStage' | 'byMonth'>;
  title: string;
  column: string;
  drill?: Drill;
}

const REPORTS: ReportSpec[] = [
  { key: 'byGrade', title: 'By grade', column: 'Grade', drill: (r) => ({ grade: r.key }) },
  {
    key: 'byStage',
    title: 'By applicant stage',
    column: 'Stage',
    drill: (r) => ({ stage: r.key as ApplicantStage }),
  },
  {
    key: 'byTrustee',
    title: 'By trustee',
    column: 'Trustee',
    drill: (r) => ({ trusteeUserId: r.key }),
  },
  { key: 'byMonth', title: 'By month submitted', column: 'Month', drill: (r) => dateRange(r.key) },
  { key: 'bySchool', title: 'By current school', column: 'Current school' },
];

function dateRange(month: string) {
  const { from, to } = monthRange(month);
  return { submittedFrom: from, submittedTo: to };
}

function ReportTable({
  spec,
  rows,
  cycleId,
}: {
  spec: ReportSpec;
  rows: ReferralReportRow[];
  cycleId?: string;
}) {
  const href = (row: ReferralReportRow) => {
    const drill = cycleId && spec.drill?.(row);
    return drill ? referralsHref({ cycleId, ...drill }) : undefined;
  };
  return (
    <Paper component="section" aria-label={spec.title} sx={{ p: 2.5 }}>
      <Typography variant="h3" component="h2" mb={1}>
        {spec.title}
      </Typography>
      {rows.length === 0 ? (
        <Typography color="text.secondary">No submitted referrals.</Typography>
      ) : (
        <Table size="small" aria-label={`Referrals ${spec.title.toLowerCase()}`}>
          <TableHead>
            <TableRow>
              <TableCell>{spec.column}</TableCell>
              <TableCell align="right">Referrals</TableCell>
              <TableCell align="right">Children</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.key}>
                <TableCell component="th" scope="row">
                  {r.label}
                </TableCell>
                <TableCell align="right">
                  <DrillCount
                    count={r.referrals}
                    href={href(r)}
                    description={`${plural(r.referrals, 'referral', 'referrals')}, ${spec.column.toLowerCase()} ${r.label}`}
                  />
                </TableCell>
                <TableCell align="right">{r.children}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}

export function ReportsPage() {
  const { params, set } = useUrlState();
  const cycles = useCycles();
  const all = params.get('scope') === 'all';
  const cycleId = params.get('cycle') ?? cycles.data?.find((c) => c.active)?.id;
  const cycle = cycles.data?.find((c) => c.id === cycleId);
  const reports = useReferralReports(all ? undefined : cycleId, all || !!cycleId);
  const r = reports.data;

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        mb={1}
      >
        <Typography variant="h2">Referral reports</Typography>
        <TextField
          select
          size="small"
          label="Cycles"
          value={all ? 'all' : 'cycle'}
          onChange={(e) => set({ scope: e.target.value === 'all' ? 'all' : null })}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value="cycle">
            {cycle ? `Selected cycle (${cycle.label})` : 'Selected cycle'}
          </MenuItem>
          <MenuItem value="all">All cycles</MenuItem>
        </TextField>
      </Stack>
      <Box mb={2}>
        <ReportsNote />
        <Typography variant="body2" color="text.secondary">
          Children are counted once even when referred more than once; a referral counts in every
          row one of its children falls in.
        </Typography>
      </Box>
      {reports.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load referral reports: {describeError(reports.error)}
        </Alert>
      )}
      {!r ? (
        !reports.error && <Skeleton variant="rounded" height={320} />
      ) : (
        <>
          <Typography mb={2} data-testid="report-totals">
            {`${r.totals.referrals} referrals · ${r.totals.families} families · ${r.totals.children} children`}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
            {REPORTS.map((spec) => (
              <ReportTable
                key={spec.key}
                spec={spec}
                rows={r[spec.key]}
                cycleId={all ? undefined : (r.cycleId ?? undefined)}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}
